'use client';
import React from 'react';
import { IBusinessGraphNode } from '@/utils/BusinessGraph';
import {
  useBusinessLocationForId,
  useBusinessLocationGraphs,
} from '@/app/(auth)/signup/components/BusinessLocationsProvider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@common/bw-ui/ui/select';
import { useFormControl } from '@common/bw-ui/ui/form-field';
import { AlertCircleSolidAnimatedIcon, SuccessCircleAnimatedIcon } from '@common/bw-ui/icons';

interface ISelectHomeLocationProps {
  homeLocation: number;
  setHomeLocation: (homeLocation: number) => void;
}

function GraphNodeOptions({ node, depth }: { node: IBusinessGraphNode; depth: number }) {
  const businessLocation = useBusinessLocationForId(node.businessId);

  if (node.isRoot) {
    return (
      <>
        {node.children.map(child => (
          <GraphNodeOptions
            key={`${node.parentBusinessId || 'root'}-${depth}-${node.businessId}-${child.businessId}-node`}
            node={child}
            depth={depth}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <SelectItem
        value={String(businessLocation?.Id ?? node.businessId)}
        disabled={!!node.children.length || !businessLocation}
        style={{ paddingLeft: `${depth + 1}rem` }} // Tried to do this with tailwind but it can't do dynamic values here
        data-locationid={node.businessId}>
        {node.name}
      </SelectItem>

      {node.children.map(child => (
        <GraphNodeOptions
          node={child}
          key={`${node.parentBusinessId || 'root'}-${depth}-${node.businessId}-${child.businessId}`}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

export const SelectHomeLocation = ({ homeLocation, setHomeLocation }: ISelectHomeLocationProps) => {
  const rootBusinesses = useBusinessLocationGraphs();
  const location = useBusinessLocationForId(homeLocation);

  // The trigger inherits aria-invalid from the enclosing FormField's error; the
  // invalid state wins over success (the trigger styles off the same attributes)
  const isInvalid = Boolean(useFormControl({})['aria-invalid']);
  const isSuccess = Boolean(location) && !isInvalid;

  return (
    <Select
      value={location ? String(location.Id) : undefined}
      onValueChange={value => value && setHomeLocation(Number(value))}>
      <SelectTrigger
        aria-label="Your Home Location"
        data-testid="home-location-select"
        data-success={isSuccess ? '' : undefined}>
        <SelectValue placeholder="Select a location" />
        {/* ml-auto keeps the status icon pinned next to the chevron (auto
            margins win over the trigger's justify-between) */}
        {isSuccess && (
          <SuccessCircleAnimatedIcon
            data-slot="success-icon"
            className="tw:ml-auto tw:size-5 tw:shrink-0 tw:text-success"
            aria-hidden="true"
          />
        )}
        {isInvalid && (
          <AlertCircleSolidAnimatedIcon
            data-slot="error-icon"
            className="tw:ml-auto tw:size-5 tw:shrink-0 tw:text-destructive"
            aria-hidden="true"
          />
        )}
      </SelectTrigger>
      <SelectContent>
        {rootBusinesses.map(b => (
          <GraphNodeOptions node={b} depth={0} key={`${b.parentBusinessId || 'root'}-0-${b.businessId}`} />
        ))}
      </SelectContent>
    </Select>
  );
};
